import { useCallback, useRef, memo } from 'react';
import type { FC } from 'react';
import type { BundleChunk, RangeSelection, TranslationState } from './types';
import { useSelection } from './useSelection';
import { useSelectionHistory } from './SelectionHistoryContext';
import { useLongPress } from './useLongPress';
import { useSpeechmarks } from './useSpeechmarks';
import TokenSpan from './TokenSpan';
import { translationService } from './translation-service';
import { useVocabMethods } from './VocabContext';
import { calculateTranslationCost } from '@polyreader/shared';
import { useToast } from '../../contexts/ToastContext';
import { useBalance } from '../../contexts/BalanceContext';
import { useTranslation } from '../../i18n/I18nContext';

interface ChunkRendererProps {
  articleId: string;
  chunkIndex: number;
  chunk: BundleChunk;
  sourceLanguage?: string;
  targetLanguage?: string;
  isActiveChunk?: boolean;
  isAudioPlaying?: boolean;
  getCurrentTime?: () => number;
}

const tagStyles: Record<string, string> = {
  h1: 'text-2xl font-bold mb-4 break-words',
  h2: 'text-xl font-semibold mb-3 break-words',
  h3: 'text-lg font-semibold mb-2 break-words',
  h4: 'text-base font-semibold mb-2 break-words',
  h5: 'text-sm font-semibold mb-1 break-words',
  h6: 'text-sm font-medium mb-1 break-words',
};

const ChunkRenderer: FC<ChunkRendererProps> = ({
  articleId,
  chunkIndex,
  chunk,
  sourceLanguage,
  targetLanguage,
  isActiveChunk,
  isAudioPlaying,
  getCurrentTime,
}) => {
  const { showToast } = useToast();
  const { t } = useTranslation();
  const { balance } = useBalance();
  const { addEntry } = useVocabMethods();
  const { getChunkSelections, onChunkSelectionsChange, revision, hidden, toggleHidden } = useSelectionHistory();

  const { tag, text, nodes: tokens } = chunk;

  const { isNodeRevealed } = useSpeechmarks({
    tokens,
    isPlaying: (isActiveChunk && isAudioPlaying) ?? false,
    getCurrentTime: getCurrentTime ?? (() => 0),
  });

  // Ref bridges the forward-reference gap: handleTranslationRequest is defined
  // before useSelection (which provides updateTranslation). Without the ref,
  // the memoized callback captures a stale or uninitialised binding.
  const updateTranslationRef = useRef<(id: string, translation: string, state: TranslationState) => void>(null!);

  const handleTranslationRequest = useCallback(
    async (selection: RangeSelection) => {
      const update = updateTranslationRef.current!;
      const isSingle = selection.startIndex === selection.endIndex;

      if (!sourceLanguage || !targetLanguage) return;

      // Single word with preloaded translation: add vocab entry directly
      if (isSingle) {
        const token = tokens[selection.startIndex];
        if (token?.translation) {
          if (selection.translationState !== 'translated') {
            update(selection.selectionId, token.translation, 'translated');
          }
          addEntry({
            articleId,
            chunkId: chunkIndex,
            text: selection.text,
            translation: token.translation,
            sourceLanguage,
            targetLanguage,
            context: selection.chunkText,
            partOfSpeech: token.partOfSpeech,
            normalizedForm: token.normalizedForm,
            grammarCase: token.grammarCase,
            voice: token.voice,
            gender: token.gender,
            aspect: token.aspect,
            number: token.number,
            tense: token.tense,
            mood: token.mood,
            inflections: token.inflections,
          }).catch(err => console.error('Vocab save failed:', err));
          return;
        }
      }

      // Already translated (phrase with cached translation) — nothing more to do
      if (selection.translationState === 'translated') return;

      // Skip untranslatable content
      if (translationService.isUntranslatable(selection.text)) {
        update(selection.selectionId, selection.text, 'untranslatable');
        return;
      }

      // Client-side balance pre-check for paid translations
      if (balance) {
        const { costCents } = calculateTranslationCost(selection.text.length);
        if (balance.effectiveBalance < costCents) {
          update(selection.selectionId, '', 'untranslatable');
          showToast(t('translation.insufficient_funds'), 'error');
          return;
        }
      }

      // API translation
      const result = await translationService.translate({
        text: selection.text,
        sourceLanguage,
        targetLanguage,
        context: selection.chunkText,
      });

      if (result.success && result.translation) {
        update(selection.selectionId, result.translation, 'translated');

        const firstToken = isSingle ? tokens[selection.startIndex] : undefined;
        addEntry({
          articleId,
          chunkId: chunkIndex,
          text: selection.text,
          translation: result.translation,
          sourceLanguage,
          targetLanguage,
          context: selection.chunkText,
          partOfSpeech: firstToken?.partOfSpeech,
          normalizedForm: firstToken?.normalizedForm,
          grammarCase: firstToken?.grammarCase,
          gender: firstToken?.gender,
          voice: firstToken?.voice,
          aspect: firstToken?.aspect,
          number: firstToken?.number,
          tense: firstToken?.tense,
          mood: firstToken?.mood,
          inflections: firstToken?.inflections,
        }).catch(err => console.error('Vocab save failed:', err));
      } else {
        update(selection.selectionId, '', 'untranslatable');

        // Show toast for specific error types
        if (result.errorType === 'payment_required') {
          showToast(t('translation.insufficient_funds'), 'error');
        } else if (result.errorType === 'rate_limit') {
          showToast(t('translation.rate_limit'), 'warning');
        } else if (result.errorType === 'forbidden') {
          showToast(t('translation.access_denied'), 'error');
        }
      }
    },
    [articleId, chunkIndex, sourceLanguage, targetLanguage, chunk, balance, showToast, t, addEntry]
  );

  const {
    handleTap,
    handleLongPress,
    updateTranslation,
    getSelectionContaining,
  } = useSelection({
    tokens,
    sourceLanguage,
    targetLanguage,
    chunkText: text,
    onTranslationRequest: handleTranslationRequest,
    initialSelections: getChunkSelections(chunkIndex),
    onSelectionsChange: (sels) => onChunkSelectionsChange(chunkIndex, sels),
    revision,
  });

  // Keep ref in sync so the callback always has the latest updateTranslation
  updateTranslationRef.current = updateTranslation;

  const isChunkPlaying = !!(isAudioPlaying && isActiveChunk);

  // Skip taps on punctuation-only tokens (they can still be part of phrase selections)
  // During audio playback, only allow interaction with revealed (unmuted) nodes
  // In hidden mode, any interaction auto-unhides and then processes normally
  const filteredTap = useCallback(
    (nodeIndex: number) => {
      if (isChunkPlaying && !isNodeRevealed(nodeIndex)) return;
      const token = tokens[nodeIndex];
      if (token && /^[\p{P}\p{S}\s]+$/u.test(token.text)) return;
      if (hidden) toggleHidden();
      handleTap(nodeIndex);
    },
    [tokens, handleTap, isChunkPlaying, isNodeRevealed, hidden, toggleHidden]
  );

  const filteredLongPress = useCallback(
    (nodeIndex: number) => {
      if (isChunkPlaying && !isNodeRevealed(nodeIndex)) return;
      if (hidden) toggleHidden();
      handleLongPress(nodeIndex);
    },
    [handleLongPress, isChunkPlaying, isNodeRevealed, hidden, toggleHidden]
  );

  const { getHandlers } = useLongPress({
    onTap: filteredTap,
    onLongPress: filteredLongPress,
  });

  const className = tagStyles[tag] || 'text-base mb-2 leading-6 break-words';

  return (
    <div className={className} style={{ wordSpacing: '-0.05em' }}>
      {tokens.map((token, i) => {
        // During audio playback, suppress selections on muted (unrevealed)
        // tokens so they unmute individually via speechmarks. For phrase
        // selections, wait until ALL tokens in the range are revealed.
        const isMuted = isChunkPlaying && !isNodeRevealed(i);
        const rawSelection = isMuted ? undefined : getSelectionContaining(i);
        const selection = rawSelection && isChunkPlaying && rawSelection.startIndex !== rawSelection.endIndex
          ? (isNodeRevealed(rawSelection.endIndex) ? rawSelection : undefined)
          : rawSelection;

        // Phrase selections render as a single span at startIndex in both
        // normal and hidden modes — the sibling tokens are suppressed so
        // the phrase appears as one contiguous highlight rather than a
        // row of adjacent badges with literal spaces between them.
        const isRangeHidden =
          selection !== undefined &&
          selection.startIndex !== selection.endIndex &&
          i !== selection.startIndex;

        return (
          <span key={i}>
            <TokenSpan
              text={token.text}
              nodeIndex={i}
              selection={i === selection?.startIndex ? selection : undefined}
              isRangeHidden={isRangeHidden}
              handlers={getHandlers(i)}
              isMuted={isMuted}
              hidden={hidden}
            />
            {i < tokens.length - 1 && ' '}
          </span>
        );
      })}
    </div>
  );
};

export default memo(ChunkRenderer);
